import { Bars3Icon, Cog6ToothIcon } from "@heroicons/react/24/outline";
import { ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import AppSidebar from "../navigation/app-sidebar";
import { Logo } from "../shared/brand";
import { SubmitButton } from "../shared/buttons/submit-button";
import { cn } from "../utls/utils";

type AppShellProps = {
  isAuthenticated: boolean;
  isMobileNavOpen: boolean;
  onOpenMobileNav: () => void;
  onCloseMobileNav: () => void;
  isAdmin: boolean;
  children: ReactNode;
};

export function AppShell({
  isAuthenticated,
  isMobileNavOpen,
  onOpenMobileNav,
  onCloseMobileNav,
  isAdmin,
  children,
}: AppShellProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [isSigningOut, setSigningOut] = useState(false);

  const handleLogout = async () => {
    setSigningOut(true);
    await signOut();
    navigate("/login", { replace: true });
    setSigningOut(false);
  };

  return (
    <div className="flex min-h-screen">
      {isAuthenticated && (
        <AppSidebar
          isMobileOpen={isMobileNavOpen}
          onClose={onCloseMobileNav}
          isAdmin={isAdmin}
        />
      )}

      <div
        className={cn(
          "flex min-h-screen flex-1 flex-col",
          isAuthenticated && "lg:pl-72"
        )}
      >
        {isAuthenticated && (
          <>
            {/* Mobile header */}
            <div className="sticky top-0 z-30 flex items-center justify-between border-b border-cream-200 bg-cream px-4 py-3 shadow-sm lg:hidden">
              <button
                type="button"
                onClick={onOpenMobileNav}
                className="inline-flex items-center gap-2 rounded-lg border border-cream-400 px-3 py-2 text-sm font-medium text-brown-700"
                aria-label="Open navigation"
              >
                <Bars3Icon className="h-5 w-5" />
                Menu
              </button>
              <Link to="/" className="flex items-center gap-2">
                <Logo size="xs" textClassName="text-terracotta-600" imageOnly />
              </Link>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="rounded-lg border border-terracotta-200 px-3 py-1 text-sm font-medium text-terracotta-700 hover:bg-terracotta-50"
                    aria-label="Open admin tools"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  className="rounded-full border border-transparent p-2 text-brown-600 hover:text-terracotta-700"
                  aria-label="Account settings"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                </Link>
                <SubmitButton
                  size="sm"
                  color="beige"
                  hideArrow
                  onClick={handleLogout}
                  isLoading={isSigningOut}
                >
                  {isSigningOut ? "Signing out" : "Log out"}
                </SubmitButton>
              </div>
            </div>
            {/* Large screen header */}
            <div className="hidden lg:sticky lg:top-0 lg:z-30 lg:flex lg:items-center lg:justify-end lg:border-b lg:border-cream-200 lg:bg-cream lg:px-6 lg:py-3 lg:shadow-sm">
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="rounded-lg border border-terracotta-200 px-3 py-1 text-sm font-medium text-terracotta-700 hover:bg-terracotta-50"
                    aria-label="Open admin tools"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  className="rounded-full border border-transparent p-2 text-brown-600 hover:text-terracotta-700"
                  aria-label="Account settings"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                </Link>
                <SubmitButton
                  size="sm"
                  color="beige"
                  hideArrow
                  onClick={handleLogout}
                  isLoading={isSigningOut}
                >
                  {isSigningOut ? "Signing out" : "Log out"}
                </SubmitButton>
              </div>
            </div>
          </>
        )}

        <main className="flex-1 pb-32">{children}</main>
      </div>
    </div>
  );
}

export default AppShell;
